<script setup>
  import { onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import ExternalPublicationAlert from '~/components/publication/ExternalPublicationAlert.vue'
  import PublicationTypeValues from '~/values/PublicationTypeValues.js'
  import OpenTicketPaymentProcessTypeValues from '../../values/OpenTicketPaymentProcessTypeValues.js'
  import { useMainStore } from '~/stores/main-store'

  const props = defineProps({
    publicationData: {
      required: true,
      type: Object,
    },
  })

  const mainStore = useMainStore()
  const route = useRoute()
  const currencies = useState('currencies')
  const selectedCurrency = ref()
  const { locale } = useI18n()
  const { $axios, $dayjs } = useNuxtApp()
  const { type, dateId } = route.params
  const isFromTicketsRoute =
    route.name && route.name.startsWith('type-tickets-dateId-tickets')

  const fetchPublication = ref(true)
  const publicationExternalPlatformDialog = ref(null)
  const profileDialog = ref(null)
  const publicationProducerDialog = ref(null)
  const artists = ref([])
  const producers = ref([])
  const fetchPublicationOpenTickets = ref(false)
  const openTicketQtyDialog = ref(null)
  const openTicketList = ref([])

  const publicationDateData = computed(() => {
    return props.publicationData?.dates.find(
      (date) => Number(date.id) === Number(dateId)
    )
  })

  const createMinItemsDisplay = (minItems = 2) => {
    return {
      getMinItems: (items) => {
        const minItemsList = []
        items.forEach((item) => {
          if (minItemsList.length < minItems) {
            minItemsList.push(item)
          }
        })
        return minItemsList
      },

      getQtyResumed: (items) => {
        if (items.length > minItems) {
          return `y ${items.length - minItems} más...`
        }
        return ''
      },
    }
  }

  const artistsDisplay = createMinItemsDisplay(2)
  const getMinArtists = computed(() =>
    artistsDisplay.getMinItems(artists.value)
  )
  const getArtistQtyResumed = computed(() =>
    artistsDisplay.getQtyResumed(artists.value)
  )

  const producersDisplay = createMinItemsDisplay(2)
  const getMinProducers = computed(() =>
    producersDisplay.getMinItems(producers.value)
  )
  const getProducerQtyResumed = computed(() =>
    producersDisplay.getQtyResumed(producers.value)
  )

  onMounted(() => {
    setPublicationArtists()
    setPublicationProducers()
    if (isFromTicketsRoute) {
      getOpenTickets()
    }
  })

  watch(
    () => [mainStore.currencySelected, currencies.value],
    () => {
      selectedCurrency.value = currencies.value?.find(
        (currency) =>
          currency.code === sessionStorage.getItem('currencySelected')
      )
    },
    { immediate: true, deep: true }
  )

  const showExternalPlatform = computed(() => {
    if (
      publicationDateData.value &&
      publicationDateData.value?.show_external_platform_url
    ) {
      return (
        Object.keys(publicationDateData.value?.external_platform).length > 0
      )
    }

    return false
  })

  const getPublicationCoverImage = computed(() => {
    return !dateId
      ? props.publicationData?.cover_image_url
      : publicationDateData.value?.cover_image_url
  })

  const getPublicationDateFormat = computed(() => {
    const dateFormat = 'dddd D MMMM'
    if (publicationDateData.value.show_end_date &&
      publicationDateData.value.start_date !==
      publicationDateData.value.end_date
    ) {
      return (
        $dayjs(publicationDateData.value.start_date).format(dateFormat) +
        ' - ' +
        $dayjs(publicationDateData.value.end_date).format(dateFormat)
      )
    } else {
      return $dayjs(publicationDateData.value.start_date).format(dateFormat)
    }
  })

  const getPublicationHourFormat = computed(() => {
    if (
      publicationDateData.value.show_end_date &&
      publicationDateData.value.start_date !==
      publicationDateData.value.end_date
    ) {
      return (
        $dayjs(publicationDateData.value.start_date).format('hh:mm A') +
        ' - ' +
        $dayjs(publicationDateData.value.end_date).format('hh:mm A')
      )
    }

    return $dayjs(publicationDateData.value.start_date).format('h:mm A')
  })

  const getDoorOpenTimeFormat = computed(() => {
    if (publicationDateData.value?.door_open_time) {
      const openTimeDate = $dayjs(publicationDateData.value.start_date).format('YYYY-MM-DD')
      return $dayjs(`${openTimeDate} ${publicationDateData.value?.door_open_time}`).format('h:mm A')
    }

    return ''
  })

  function setPublicationArtists() {
    const publicationArtists = []
    const artistSet = new Set()

    if (!dateId) {
      props.publicationData?.dates.forEach((date) => {
        if (date?.artists?.length > 0) {
          date?.artists.forEach((artist) => {
            if (!artistSet.has(artist?.artist_id)) {
              publicationArtists.push(artist)
              artistSet.add(artist?.artist_id)
            }
          })
        }
      })
    } else {
      if (publicationDateData.value?.artists?.length > 0) {
        publicationDateData.value?.artists.forEach((artist) => {
          if (!artistSet.has(artist?.artist_id)) {
            publicationArtists.push(artist)
            artistSet.add(artist?.artist_id)
          }
        })
      }
    }

    artists.value = publicationArtists
  }

  function setPublicationProducers() {
    const publicationProducers = []
    const producerSet = new Set()

    if (!dateId) {
      props.publicationData?.dates.forEach((date) => {
        if (date?.producers?.length > 0) {
          date?.producers.forEach((producer) => {
            if (!producerSet.has(producer?.producer_id)) {
              publicationProducers.push(producer)
              producerSet.add(producer?.producer_id)
            }
          })
        }
      })
    } else {
      if (publicationDateData.value?.producers?.length > 0) {
        publicationDateData.value?.producers.forEach((producer) => {
          if (!producerSet.has(producer?.producer_id)) {
            publicationProducers.push(producer)
            producerSet.add(producer?.producer_id)
          }
        })
      }
    }

    producers.value = publicationProducers
  }

  const getOpenTickets = () => {
    fetchPublicationOpenTickets.value = true
    $axios
      .get(`open-tickets/${dateId}`)
      .then((response) => {
        fetchPublicationOpenTickets.value = false
        openTicketList.value = response.data?.open_tickets
      })
      .catch(() => {
        fetchPublicationOpenTickets.value = false
        openTicketList.value = []
      })
  }

  const showOpenTicketQtyDialog = (openTicket) => {
    console.log(openTicket)
  }

  function showProfileDialog(length, items, type) {
    if (length > 1) profileDialog.value.showDialog(items, type)
    else navigateTo(`/${type}/${items[0].username}`)
  }
</script>

<template>
  <ExternalPublicationAlert v-if="showExternalPlatform" />
  <div class="publication-detail-container">
    <div class="space-y-6 text-[19px]">
      <div
        class="w-full h-[450px] bg-cover bg-center rounded-xl"
        :style="{ backgroundImage: `url(${getPublicationCoverImage})` }"
      />
      <div v-if="publicationDateData && !isFromTicketsRoute" class="pb-4">
        <PublicationBuyButton :publicationDateData="publicationDateData" />
      </div>
      <!-- TODO: Hay que integrar estas funciones en el portal -->
      <!--<div>
        <div class="font-semibold">Tags</div>
        <div class="text-gray-600 dark:text-gray-400">Música, Conciertos, Trap, Shows</div>
      </div>-->
      <!--<div v-if="publicationDateData">
        <div class="font-semibold">Duración Aprox.</div>
        <div class="text-gray-400">3 horas</div>
      </div>-->
      <!--<div>
        <div class="font-semibold">Publicado el:</div>
        <div class="text-gray-400">
          {{ $dayjs(publicationData?.created_at).format('dddd D MMMM YYYY') }}
        </div>
      </div>-->
      <div
        v-if="publicationDateData && publicationDateData?.show_door_open_time"
      >
        <div class="font-semibold">Apertura de Puertas</div>
        <div class="text-gray-400">{{ getDoorOpenTimeFormat }}</div>
      </div>
    </div>
    <div>
      <div>
        <div class="text-lg md:mb-5">
          {{ PublicationTypeValues.get(publicationData.type).label }}
        </div>
        <div class="text-5xl font-bold mb-2">{{ publicationData?.title }}</div>
        <div class="text-2xl text-gray-400">
          {{ publicationData.sub_title }}
        </div>
      </div>
      <div class="pt-6 space-y-2" v-if="publicationDateData">
        <div
          v-if="publicationDateData.address_enabled"
          class="flex items-center text-lg text-gray-400 space-x-4"
        >
          <div>
            <svg class="w-[24px]" viewBox="0 0 43 43" fill="none">
              <path
                d="M21.4999 11.6458C22.6879 11.6458 23.8272 12.1177 24.6672 12.9578C25.5072 13.7978 25.9791 14.9371 25.9791 16.125C25.9791 16.7132 25.8632 17.2957 25.6381 17.8391C25.413 18.3825 25.0831 18.8763 24.6672 19.2923C24.2512 19.7082 23.7575 20.0381 23.214 20.2632C22.6706 20.4883 22.0881 20.6042 21.4999 20.6042C20.312 20.6042 19.1727 20.1323 18.3327 19.2923C17.4927 18.4522 17.0208 17.313 17.0208 16.125C17.0208 14.9371 17.4927 13.7978 18.3327 12.9578C19.1727 12.1177 20.312 11.6458 21.4999 11.6458ZM21.4999 3.58333C24.8262 3.58333 28.0162 4.90468 30.3682 7.2567C32.7202 9.60872 34.0416 12.7987 34.0416 16.125C34.0416 25.5313 21.4999 39.4167 21.4999 39.4167C21.4999 39.4167 8.95825 25.5313 8.95825 16.125C8.95825 12.7987 10.2796 9.60872 12.6316 7.2567C14.9836 4.90468 18.1737 3.58333 21.4999 3.58333ZM21.4999 7.16667C19.124 7.16667 16.8454 8.11049 15.1654 9.7905C13.4854 11.4705 12.5416 13.7491 12.5416 16.125C12.5416 17.9167 12.5416 21.5 21.4999 33.5221C30.4583 21.5 30.4583 17.9167 30.4583 16.125C30.4583 13.7491 29.5144 11.4705 27.8344 9.7905C26.1544 8.11049 23.8758 7.16667 21.4999 7.16667Z"
                fill="#989898"
              />
            </svg>
          </div>
          <div>{{ publicationDateData.address }}</div>
        </div>
        <div class="flex items-center text-lg text-gray-400 space-x-4">
          <div>
            <svg class="w-[24px]" viewBox="0 0 43 43" fill="none">
              <path
                d="M12.5417 19.7083H16.125V23.2917H12.5417V19.7083ZM34.0417 5.375H32.25V1.79166H28.6667V5.375H14.3333V1.79166H10.75V5.375H8.95833C6.9875 5.375 5.375 6.9875 5.375 8.95833V34.0417C5.375 36.0125 6.9875 37.625 8.95833 37.625H34.0417C36.0125 37.625 37.625 36.0125 37.625 34.0417V8.95833C37.625 6.9875 36.0125 5.375 34.0417 5.375ZM34.0417 8.95833V12.5417H8.95833V8.95833H34.0417ZM8.95833 34.0417V16.125H34.0417V34.0417H8.95833ZM19.7083 26.875H23.2917V30.4583H19.7083V26.875ZM26.875 26.875H30.4583V30.4583H26.875V26.875ZM26.875 19.7083H30.4583V23.2917H26.875V19.7083Z"
                fill="#8A8A8A"
              />
            </svg>
          </div>
          <div>{{ getPublicationDateFormat }}</div>
        </div>
        <div class="flex items-center text-lg text-gray-400 space-x-4">
          <div>
            <svg class="w-[24px]" viewBox="0 0 43 43" fill="none">
              <path
                d="M21.4999 35.8333C29.3833 35.8333 35.8333 29.3833 35.8333 21.5C35.8333 13.6167 29.3833 7.16666 21.4999 7.16666C13.6166 7.16666 7.16659 13.6167 7.16659 21.5C7.16659 29.3833 13.6166 35.8333 21.4999 35.8333ZM21.4999 3.58333C31.3541 3.58333 39.4166 11.6458 39.4166 21.5C39.4166 31.3542 31.3541 39.4167 21.4999 39.4167C11.6458 39.4167 3.58325 31.3542 3.58325 21.5C3.58325 11.6458 11.6458 3.58333 21.4999 3.58333ZM22.3958 22.9333L13.7958 27.95L12.5416 25.4417L19.7083 21.3208V12.5417H22.3958V22.9333Z"
                fill="#8A8A8A"
              />
            </svg>
          </div>
          <div>{{ getPublicationHourFormat }}</div>
        </div>
      </div>
      <div
        class="pt-6 space-y-3"
        v-if="getMinProducers.length > 0 || getMinArtists.length > 0"
      >
        <div
          class="flex items-center relative cursor-pointer"
          v-if="getMinProducers.length"
          @click="
            showProfileDialog(getMinProducers.length, producers, 'producer')
          "
        >
          <div
            v-for="(producer, producerIndex) in getMinProducers"
            :key="producerIndex"
            class="relative w-8"
            :style="{ marginLeft: `${producerIndex * -5}px` }"
          >
            <img
              class="rounded-full border-2 border-gray-100"
              :src="producer.photo_url"
              :alt="producer.username"
            />
            <CheckDecagramIcon
              v-if="producer?.is_verified"
              class="absolute bottom-0 right-[-4px] w-[14px]"
            />
          </div>
          <div class="flex items-center overlapping" style="margin-left: 10px">
            <div class="font-bold text-lg">
              {{ getMinProducers.map((producer) => producer.name).join(', ') }}
              {{ getProducerQtyResumed }}
            </div>
          </div>
        </div>
        <div
          class="flex items-center relative cursor-pointer"
          v-if="getMinArtists.length"
          @click="showProfileDialog(getMinArtists.length, artists, 'artist')"
        >
          <div
            v-for="(artist, artistIndex) in getMinArtists"
            :key="artistIndex"
            class="relative w-8"
            :style="{ marginLeft: `${artistIndex * -5}px` }"
          >
            <img
              class="rounded-full border-2 border-gray-100"
              :src="artist.photo_url"
              :alt="artist.username"
            />
            <CheckDecagramIcon
              v-if="artist?.is_verified"
              class="absolute bottom-0 right-[-4px] w-[14px]"
            />
          </div>
          <div class="flex items-center ml-5">
            <div class="font-bold text-lg">
              {{ getMinArtists.map((artist) => artist.name).join(', ') }}
              {{ getArtistQtyResumed }}
            </div>
          </div>
        </div>
      </div>
      <hr
        class="border-gray-100 dark:border-gray-700 my-7"
        v-if="publicationDateData"
      />
      <div class="w-full md:w-8/12 space-y-6 mt-7">
        <div class="space-y-2" v-if="!publicationDateData">
          <div class="text-[18px] font-semibold">Fechas</div>
          <NuxtLink
            v-for="date in publicationData?.dates"
            :to="`/${locale}/${type}-tickets/${date?.id}`"
            :key="date?.id"
            class="flex justify-between items-center p-5 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-all"
          >
            <div class="flex flex-col space-y-3">
              <div class="flex items-center space-x-3">
                <div>
                  <svg class="w-[28px]" viewBox="0 0 43 43" fill="none">
                    <path
                      d="M12.5417 19.7083H16.125V23.2917H12.5417V19.7083ZM34.0417 5.37501H32.25V1.79167H28.6667V5.37501H14.3333V1.79167H10.75V5.37501H8.95833C6.9875 5.37501 5.375 6.98751 5.375 8.95834V34.0417C5.375 36.0125 6.9875 37.625 8.95833 37.625H34.0417C36.0125 37.625 37.625 36.0125 37.625 34.0417V8.95834C37.625 6.98751 36.0125 5.37501 34.0417 5.37501ZM34.0417 8.95834V12.5417H8.95833V8.95834H34.0417ZM8.95833 34.0417V16.125H34.0417V34.0417H8.95833ZM19.7083 26.875H23.2917V30.4583H19.7083V26.875ZM26.875 26.875H30.4583V30.4583H26.875V26.875ZM26.875 19.7083H30.4583V23.2917H26.875V19.7083Z"
                      fill="#8A8A8A"
                    />
                  </svg>
                </div>
                <div class="font-semibold text-[16px]">
                  {{ $dayjs(date?.start_date).format('dddd D MMMM hh:mm A') }}
                </div>
              </div>
              <div class="flex items-center space-x-3">
                <div>
                  <svg class="w-[28px]" viewBox="0 0 43 43" fill="none">
                    <path
                      d="M21.5 11.6458C22.688 11.6458 23.8273 12.1178 24.6673 12.9578C25.5073 13.7978 25.9792 14.9371 25.9792 16.125C25.9792 16.7132 25.8634 17.2957 25.6383 17.8391C25.4132 18.3826 25.0832 18.8763 24.6673 19.2923C24.2514 19.7082 23.7576 20.0381 23.2141 20.2632C22.6707 20.4883 22.0883 20.6042 21.5 20.6042C20.3121 20.6042 19.1728 20.1323 18.3328 19.2923C17.4928 18.4523 17.0209 17.313 17.0209 16.125C17.0209 14.9371 17.4928 13.7978 18.3328 12.9578C19.1728 12.1178 20.3121 11.6458 21.5 11.6458ZM21.5 3.58334C24.8263 3.58334 28.0163 4.90469 30.3683 7.25671C32.7204 9.60873 34.0417 12.7988 34.0417 16.125C34.0417 25.5313 21.5 39.4167 21.5 39.4167C21.5 39.4167 8.95837 25.5313 8.95837 16.125C8.95837 12.7988 10.2797 9.60873 12.6317 7.25671C14.9838 4.90469 18.1738 3.58334 21.5 3.58334ZM21.5 7.16668C19.1241 7.16668 16.8456 8.1105 15.1655 9.79051C13.4855 11.4705 12.5417 13.7491 12.5417 16.125C12.5417 17.9167 12.5417 21.5 21.5 33.5221C30.4584 21.5 30.4584 17.9167 30.4584 16.125C30.4584 13.7491 29.5146 11.4705 27.8345 9.79051C26.1545 8.1105 23.8759 7.16668 21.5 7.16668Z"
                      fill="#989898"
                    />
                  </svg>
                </div>
                <div class="opacity-50">
                  {{ date.address_enabled ? date.address : '--' }}
                </div>
              </div>
            </div>
            <div class="flex items-center">
              <i
                class="mdi mdi-chevron-right text-gray-700 dark:text-white text-4xl leading-3"
              />
            </div>
          </NuxtLink>
        </div>
        <div
          class="space-y-2"
          v-if="publicationDateData && !isFromTicketsRoute"
        >
          <div class="text-[18px] font-semibold">Descripción</div>
          <div class="text-gray-400" v-html="publicationData?.description" />
        </div>
        <div class="space-y-6" v-if="publicationDateData && isFromTicketsRoute">
          <div class="text-[18px] font-semibold">Tickets</div>
          <div
            v-for="openTicket in openTicketList"
            @click="showOpenTicketQtyDialog(openTicket)"
            :key="openTicket?.id"
            class="flex select-none justify-between items-center p-5 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-all cursor-pointer"
          >
            <div class="flex items-center">
              <div
                class="flex justify-center items-center bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-1 min-w-[75px] font-semibold text-xl w-full h-16"
              >
                <template
                  v-if="
                    openTicket.payment_process_type ===
                    OpenTicketPaymentProcessTypeValues.PAY
                  "
                >
                  {{
                    `${selectedCurrency?.symbol || '$'}${
                      (openTicket.price *
                        (selectedCurrency?.exchange_rate || 1)) /
                      100
                    }`
                  }}
                  <!-- {{ currencies }} -->
                </template>
                <template
                  v-if="
                    openTicket.payment_process_type ===
                    OpenTicketPaymentProcessTypeValues.FREE
                  "
                >
                  Gratis
                </template>
                <template
                  v-if="
                    openTicket.payment_process_type ===
                    OpenTicketPaymentProcessTypeValues.DONATION
                  "
                >
                  Donativo
                </template>
              </div>
              <div class="flex flex-col ml-6 space-y-1.5">
                <div class="text-xl">
                  <i
                    :style="{ color: openTicket.color }"
                    class="mdi mdi-circle"
                  />
                  {{ openTicket.name }}
                </div>
                <div class="text-gray-400">
                  {{ openTicket.available_qty }} Boletos Disponibles
                </div>
              </div>
            </div>
            <div class="flex items-center">
              <i
                class="mdi mdi-chevron-right text-gray-700 dark:text-white text-4xl leading-3"
              />
            </div>
          </div>
        </div>
        <!--<div class="space-y-2 mt-10" v-if="publicationDateData">
          <div class="text-[18px] font-semibold">Galería</div>
          <PublicationGalleryCarousel :images="publicationImages"/>
        </div>-->
        <div
          class="space-y-2 mt-10"
          v-if="
            publicationDateData &&
            !isFromTicketsRoute &&
            publicationDateData?.sponsors.length > 0
          "
        >
          <div class="text-[18px] font-semibold">Patrocinadores</div>
          <PublicationSponsorCarousel
            :sponsors="publicationDateData?.sponsors"
          />
        </div>
        <div class="space-y-5 mt-10 w-8/12">
          <div class="text-[18px] font-semibold">Soporte</div>
          <div class="flex flex-col space-y-3">
            <div class="flex justify-between items-center space-x-3">
              <div class="flex items-center space-x-3">
                <svg
                  class="w-[20px] fill-gray-700 dark:fill-white"
                  viewBox="0 0 30 30"
                  fill="none"
                >
                  <path
                    d="M15 2.5L1.25 26.25H28.75M15 7.5L24.4125 23.75H5.5875M13.75 12.5V17.5H16.25V12.5M13.75 20V22.5H16.25V20"
                  />
                </svg>
                <div>Reportar un error</div>
              </div>
              <svg
                class="w-[20px] fill-gray-700 dark:fill-white"
                viewBox="0 0 30 30"
                fill="none"
              >
                <path
                  d="M10.7375 20.725L16.4625 15L10.7375 9.2625L12.5 7.5L20 15L12.5 22.5L10.7375 20.725Z"
                />
              </svg>
            </div>
            <hr class="border-gray-200 dark:border-gray-700" />
            <div class="flex justify-between items-center space-x-3">
              <div class="flex items-center space-x-3">
                <svg
                  class="w-[20px] fill-gray-700 dark:fill-white"
                  viewBox="0 0 30 30"
                  fill="none"
                >
                  <path
                    d="M16.25 12.5H13.75V7.5H16.25V12.5ZM16.25 15H13.75V17.5H16.25V15ZM27.5 5V20C27.5 20.663 27.2366 21.2989 26.7678 21.7678C26.2989 22.2366 25.663 22.5 25 22.5H7.5L2.5 27.5V5C2.5 4.33696 2.76339 3.70107 3.23223 3.23223C3.70107 2.76339 4.33696 2.5 5 2.5H25C25.663 2.5 26.2989 2.76339 26.7678 3.23223C27.2366 3.70107 27.5 4.33696 27.5 5ZM25 5H5V21.5L6.5 20H25V5Z"
                  />
                </svg>
                <div>Abrir un ticket</div>
              </div>
              <svg
                class="w-[20px] fill-gray-700 dark:fill-white"
                viewBox="0 0 30 30"
                fill="none"
              >
                <path
                  d="M10.7375 20.725L16.4625 15L10.7375 9.2625L12.5 7.5L20 15L12.5 22.5L10.7375 20.725Z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ProfileDialog ref="profileDialog" />
</template>
